// frontend/src/components/utilities/ProcessedImageBox.js
import React, { useEffect, useRef, useState } from 'react';
import AIColorCleanupOverlay from '../AIColorCleanupOverlay';

/**
 * ProcessedImageBox
 * Renders the processed/base layer + overlay layer in a single “box”.
 * Includes:
 * - Sliding Advanced Controls overlay panel (header + content)
 * - Optional AI button + AIColorCleanupOverlay mounting (owned here)
 * - Optional rendering.gif overlay
 * - Optional Keychain overlay button + Keychain canvas layer (owned here, logic in parent)
 *
 * Parent owns:
 * - image URLs, advanced panel state, renderAdvancedControls()
 * - AI eligibility flags + onAIConfirm behavior
 * - KC state machine + canvas drawing logic + event handlers
 *
 * This component owns:
 * - AI overlay open/close state
 * - "Used this session" behavior (button disappears after first click)
 */
export default function ProcessedImageBox({
  // Visibility
  visible = true,

  // Images
  processedImage,
  overlayImage,

  // Identity / project context (NEW)
  userEmail,
  filename,
  projectRoot,

  // Load callbacks
  onProcessedLoad,
  onOverlayLoad,

  // Box styling helpers (optional)
  boxStyle,
  layerStyle,

  // Processing state
  isProcessing = false,
  uiDisabled = false,

  // Advanced panel
  showAdvancedOverlayPanel,
  setShowAdvancedOverlayPanel,
  showColorControls,
  setShowColorControls,
  renderAdvancedControls, // function returning JSX

  // Capitan hook
  capitanApi,

  // AI button / overlay
  showAIButton = false,
  aiButtonDisabled = false,
  aiButtonTitle = '',
  aiButtonImageSrc = '/AIbutton.svg',
  onAIConfirm, // async (...args) => void
  onAIDone,
  // Optional: allow parent to reset "session" (e.g., new upload / new project)
  resetSessionSignal = null,

  // Optional: allow parent to auto-open AI overlay once (if desired)
  autoOpenAIOverlaySignal = null,

  // ─────────────────────────────────────────────
  // Keychain (KC) overlay support (optional)
  // ─────────────────────────────────────────────
  enableKC = false,
  kcContainerRef = null,
  onKcHoverChange = null,

  showKCOverlay = false,
  kcOverlayImageSrc = '/KCoverlay.svg',
  onKCOverlayClick = null,

  kcEnabled = false,
  kcCanvasRef = null,
  onKcCanvasMouseMove = null,
  onKcCanvasClick = null,
}) {
  const [showAIOverlay, setShowAIOverlay] = useState(false);
  const [aiUsedThisSession, setAiUsedThisSession] = useState(false);

  // Reset session when signal changes (new upload/new project)
  const lastResetRef = useRef(resetSessionSignal);
  useEffect(() => {
    if (resetSessionSignal == null) return;
    if (lastResetRef.current === resetSessionSignal) return;
    lastResetRef.current = resetSessionSignal;

    setAiUsedThisSession(false);
    setShowAIOverlay(false);
  }, [resetSessionSignal]);

  // Auto-open AI overlay once when signal changes (optional)
  const didAutoOpenAIRef = useRef(false);
  useEffect(() => {
    if (!autoOpenAIOverlaySignal) return;
    if (didAutoOpenAIRef.current) return;

    // Only auto-open if allowed/visible and not disabled
    if (showAIButton && !aiButtonDisabled && !uiDisabled) {
      didAutoOpenAIRef.current = true;
      setAiUsedThisSession(true);
      setShowAIOverlay(true);
    }
  }, [autoOpenAIOverlaySignal, showAIButton, aiButtonDisabled, uiDisabled]);

  // If box becomes hidden, close overlay to avoid dangling modals
  useEffect(() => {
    if (!visible) {
      setShowAIOverlay(false);
    }
  }, [visible]);

  if (!visible) return null;

  const _boxStyle =
    typeof boxStyle === 'function'
      ? boxStyle()
      : boxStyle || {
          width: '100%',
          height: '100%',
          border: '2px dashed #94a3b8',
          borderRadius: '10px',
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        };

  const _layerStyle =
    typeof layerStyle === 'function'
      ? layerStyle
      : (z) => ({
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          top: 0,
          left: 0,
          zIndex: z,
          pointerEvents: 'none',
        });

  // Whether the bottom-left KC/AI dock should render at all
  const showDock =
    (showAIButton && !aiUsedThisSession) || (enableKC && showKCOverlay);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <div
        ref={enableKC ? kcContainerRef : undefined}
        style={{ ..._boxStyle, position: 'relative' }}
        onMouseEnter={
          enableKC && !uiDisabled ? () => onKcHoverChange?.(true) : undefined
        }
        onMouseLeave={
          enableKC && !uiDisabled ? () => onKcHoverChange?.(false) : undefined
        }
      >
        {/* Sliding Advanced Controls overlay */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            zIndex: 30,
            background:
              'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
            color: 'white',
            transform: showAdvancedOverlayPanel
              ? 'translateY(0%)'
              : 'translateY(calc(-100% + 2.8vh))',
            transition: 'transform 0.25s ease-in-out',
            willChange: 'transform',
            fontSize: '0.8rem',
            pointerEvents: uiDisabled ? 'none' : 'auto',
            opacity: uiDisabled ? 0.6 : 1,
          }}
        >
          {/* Handle */}
          <div
            onClick={
              uiDisabled
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    setShowAdvancedOverlayPanel?.((prev) => {
                      if (!prev) capitanApi?.onOpenAdvanced?.();
                      return !prev;
                    });
                    if (!showColorControls) setShowColorControls?.(true);
                  }
            }
            style={{
              height: '2.8vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uiDisabled ? 'not-allowed' : 'pointer',
              borderBottom: '1px solid rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(112,112,112,0.4)',
            }}
          >
            <span
              style={{
                fontSize: '2.5vh',
                fontWeight: 600,
                letterSpacing: '0.03em',
              }}
            >
              {showAdvancedOverlayPanel
                ? '▲ Advanced Color Controls ▲'
                : '▼ Advanced Color Controls ▼'}
            </span>
          </div>

          {/* CONTENT */}
          <div
            style={{
              padding: showAdvancedOverlayPanel ? '8px 10px 10px' : '0 10px 0',
              maxHeight: showAdvancedOverlayPanel ? '55%' : 0,
              overflowY: 'auto',
              overflow: 'hidden',
              backgroundColor: 'rgba(112,112,112,0.5)',
              fontSize: '2vh',
              opacity: showAdvancedOverlayPanel ? 1 : 0,
              transition:
                'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
            }}
          >
            {/* CSS hooks (centralized) */}
            <style>
              {`
                .adv-select {
                  background: rgba(255,255,255,0.12) !important;
                  border: 1px solid rgba(255,255,255,0.25) !important;
                  color: #e5e7eb !important;
                  backdrop-filter: blur(4px);
                }
                .adv-select:focus {
                  outline: none;
                  border-color: rgba(148,163,184,0.9) !important;
                }
                .adv-select option {
                  background-color: rgba(15,23,42,0.6) !important;
                  color: #e5e7eb !important;
                }
                .adv-select option:checked,
                .adv-select option:focus {
                  background-color: rgba(55,65,81,0.6) !important;
                  color: #e5e7eb !important;
                }
              `}
            </style>

            <div
              style={{
                pointerEvents: uiDisabled ? 'none' : 'auto',
                opacity: uiDisabled ? 0.6 : 1,
              }}
            >
              {renderAdvancedControls ? renderAdvancedControls() : null}
            </div>
          </div>
        </div>

        {/* Base / Processed image */}
        {processedImage && (
          <img
            src={processedImage}
            alt="Processed"
            style={_layerStyle(1)}
            onLoad={onProcessedLoad}
          />
        )}

        {/* Overlay image */}
        {overlayImage && (
          <img
            src={overlayImage}
            alt="Overlay"
            style={_layerStyle(2)}
            onLoad={onOverlayLoad}
          />
        )}

        {/* Bottom-left dock: AI + KC (prevents overlap) */}
        {showDock && (
          <div
            style={{
              position: 'absolute',
              left: '0.8vh',
              bottom: '0.8vh',
              zIndex: 60,
              display: 'flex',
              flexDirection: 'row',
              gap: '0.8vh',
              alignItems: 'flex-end',
              pointerEvents: uiDisabled ? 'none' : 'auto',
            }}
          >
            {/* AI Button */}
            {showAIButton && !aiUsedThisSession && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (uiDisabled || aiButtonDisabled) return;

                  setAiUsedThisSession(true);
                  capitanApi?.onAIClick?.();
                  setShowAIOverlay(true);
                }}
                disabled={uiDisabled || aiButtonDisabled}
                title={aiButtonTitle}
                style={{
                  width: '14vmin',
                  height: '14vmin',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor:
                    uiDisabled || aiButtonDisabled ? 'not-allowed' : 'pointer',
                  opacity: uiDisabled || aiButtonDisabled ? 0.35 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="AI"
              >
                <img
                  src={aiButtonImageSrc}
                  alt="AI"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                  draggable={false}
                />
              </button>
            )}

            {/* KC Overlay Button */}
            {enableKC && showKCOverlay && (
              <img
                src={kcOverlayImageSrc}
                alt="KC Overlay"
                onClick={
                  uiDisabled
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        onKCOverlayClick?.(e);
                      }
                }
                style={{
                  width: '14vmin',
                  height: 'auto',
                  opacity: 0.88,
                  pointerEvents: uiDisabled ? 'none' : 'auto',
                  cursor: uiDisabled ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                }}
                draggable={false}
              />
            )}
          </div>
        )}

        {/* KC canvas overlay (full box) */}
        {enableKC && kcEnabled && (processedImage || overlayImage) && (
          <canvas
            ref={kcCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 15,
              cursor: uiDisabled ? 'not-allowed' : 'crosshair',
              pointerEvents: uiDisabled ? 'none' : 'auto',
              opacity: uiDisabled ? 0.6 : 1,
            }}
            onMouseMove={uiDisabled ? undefined : onKcCanvasMouseMove}
            onClick={uiDisabled ? undefined : onKcCanvasClick}
          />
        )}

        {/* Rendering overlay */}
        {isProcessing && (
          <img
            src="/rendering.gif"
            alt="Rendering Animation"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 200,
              height: 200,
              objectFit: 'contain',
              zIndex: enableKC ? 20 : 10,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* AI Overlay (owned here) */}
        <AIColorCleanupOverlay
          isOpen={showAIOverlay}
          uiDisabled={uiDisabled}
          userEmail={userEmail}
          filename={filename}
          projectRoot={projectRoot}
          onClose={() => setShowAIOverlay(false)}
          onConfirm={async (...args) => {
            try {
              await onAIConfirm?.(...args);
            } catch (err) {
              console.error('[ProcessedImageBox] onAIConfirm failed:', err);
            }
          }}
          onDone={async (result) => {
            try {
              await onAIDone?.(result);
            } catch (err) {
              console.error('[ProcessedImageBox] onAIDone failed:', err);
            } finally {
              setShowAIOverlay(false);
            }
          }}
        />
      </div>
    </div>
  );
}